import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import { useApi } from '../../hooks/useApi'
import StatsCard from '../../components/admin/StatsCard'
import StatusBadge from '../../components/StatusBadge'
import { Ticket, CheckCircle, Clock, AlertTriangle, XCircle } from 'lucide-react'

const Dashboard = () => {
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const api = useApi()

  useEffect(() => {
    api.getStats().then(setStats).catch(err => setError(err.message)).finally(() => setLoading(false))
  }, [])

  const timeAgo = (d) => {
    if (!d) return '—'
    const mins = Math.floor((Date.now() - new Date(d).getTime()) / 60000)
    if (mins < 1) return 'just now'
    if (mins < 60) return `${mins}m ago`
    const hours = Math.floor(mins / 60)
    if (hours < 24) return `${hours}h ago`
    return `${Math.floor(hours / 24)}d ago`
  }

  if (loading) {
    return <div className="flex items-center justify-center py-16"><div className="spinner w-8 h-8"></div></div>
  }

  if (error) {
    return (
      <div className="card text-center py-8">
        <p className="text-error mb-4">{error}</p>
        <button onClick={() => window.location.reload()} className="btn btn-primary">Retry</button>
      </div>
    )
  }

  if (!stats) return null

  return (
    <div className="space-y-6 animate-fade-in">
      <h1 className="text-xl font-bold text-text-primary">Dashboard</h1>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <StatsCard title="Total Codes" value={stats.total_codes || 0} icon={Ticket} color="accent" />
        <StatsCard title="Active" value={stats.active_codes || 0} icon={CheckCircle} color="success" />
        <StatsCard title="Redeemed" value={stats.redeemed_codes || 0} icon={CheckCircle} color="accent" />
        <StatsCard title="Today" value={stats.redeemed_today || stats.completed_today || 0} icon={Clock} color="warning" />
        <StatsCard title="Failed" value={(stats.expired_codes || 0) + (stats.revoked_codes || 0)} icon={XCircle} color="error" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Chart */}
        <div className="card">
          <h3 className="text-sm font-semibold text-text-secondary uppercase tracking-wider mb-4">
            Redemptions (30 Days)
          </h3>
          {stats.redemptions_by_day && stats.redemptions_by_day.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={stats.redemptions_by_day}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="date" stroke="#94a3b8" tick={{ fontSize: 11 }} />
                <YAxis stroke="#94a3b8" tick={{ fontSize: 11 }} />
                <Tooltip
                  contentStyle={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '12px' }}
                />
                <Line type="monotone" dataKey="count" stroke="#2563eb" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-56 flex items-center justify-center text-text-muted text-sm">No data yet</div>
          )}
        </div>

        {/* Top products */}
        <div className="card">
          <h3 className="text-sm font-semibold text-text-secondary uppercase tracking-wider mb-4">
            By Product
          </h3>
          {stats.top_products && stats.top_products.length > 0 ? (
            <div className="space-y-3">
              {stats.top_products.map((p) => {
                const total = stats.redeemed_codes || 1
                const pct = Math.min(Math.round((p.count / total) * 100), 100)
                return (
                  <div key={p.product_name} className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-text-primary">{p.product_name}</span>
                      <span className="text-text-muted">{p.count}</span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-1.5">
                      <div className="bg-accent rounded-full h-1.5 transition-all" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                )
              })}
            </div>
          ) : (
            <div className="h-56 flex items-center justify-center text-text-muted text-sm">No data yet</div>
          )}
        </div>
      </div>

      {/* Recent redemptions */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-text-secondary uppercase tracking-wider">Recent Redemptions</h3>
          <Link to="/co/redemptions" className="text-accent text-sm hover:underline">View all →</Link>
        </div>
        {stats.recent_redemptions && stats.recent_redemptions.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr><th>Product</th><th>User</th><th>Status</th><th>When</th></tr>
              </thead>
              <tbody>
                {stats.recent_redemptions.map((r) => (
                  <tr key={r.id}>
                    <td className="text-text-primary text-sm">{r.product_name}</td>
                    <td className="text-text-primary text-sm">{r.user_input || '—'}</td>
                    <td><StatusBadge status={r.status} size="xs" /></td>
                    <td className="text-text-muted text-xs">{timeAgo(r.redeemed_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8 text-text-muted text-sm">No redemptions yet</div>
        )}
      </div>
    </div>
  )
}

export default Dashboard
